/**
 * @Author: Anandp
 * @Date:   2024-04-17 18:40:49
 * @Last Modified by:   Anandp
 * @Last Modified time: 2025-01-13 22:44:23
 */
import { createContext, useCallback, useEffect, useReducer, useState } from "react";
import PropTypes from "prop-types";
import { authApi } from "src/api/auth";
import axios from "axios";
import * as msal from "@azure/msal-browser";
import { Url, siteConstant } from "src/config";
import { loginRequest } from "src/libs/auth-config";
import loginApiService from "src/services/loginVerifyAPI";
import CoachesCornerApiServices from 'src/services/coachesCornerApi';
import { useNavigate } from "react-router";
import customerApiService from "src/services/customerApi";
import CryptoJS from 'crypto-js';
import mediaUploadApis from "src/services/mediaUploadApi";

let pc = null;
let issuer = null;

var ActionType;
(function (ActionType) {
  ActionType["INITIALIZE"] = "INITIALIZE";
  ActionType["SIGN_IN"] = "SIGN_IN";
  ActionType["SIGN_UP"] = "SIGN_UP";
  ActionType["SIGN_OUT"] = "SIGN_OUT";
  ActionType["SET_ERROR_CASE"] = "SET_ERROR_CASE";
})(ActionType || (ActionType = {}));

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  accessToken: null,
  errorCase: false
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user, issuer } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      issuer
    };
  },
  SIGN_IN: (state, action) => {
    const { user, isAuthenticated, errorCase } = action.payload;

    return {
      ...state,
      isAuthenticated: isAuthenticated,
      user,
      errorCase: errorCase,
    };
  },
  SIGN_UP: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  SET_ERROR_CASE: (state, action) => {
    return {
      ...state,
      errorCase: action.payload,
    };
  },  
  SIGN_OUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

export const AuthContext = createContext({
  ...initialState,
  initialize: () => Promise.resolve(),
  signIn: () => Promise.resolve(),
  signUp: () => Promise.resolve(),
  signOut: () => Promise.resolve(),
  getAccessTocken: () => Promise.resolve(),
  setErrorCase: () => {},
});

export const AuthProvider = (props) => {
  const { children } = props;
  issuer = localStorage.getItem(siteConstant.AUTH_ISSUER_STORAGE_KEY);
  // eslint-disable-next-line
  const [publicClient, setPublicClient] = useState(null);
  const [state, dispatch] = useReducer(reducer, initialState);
  const searchParams = new URLSearchParams(window.location.search);
  const islLoginParam = searchParams.get('isllogin');
  const userLoginParam = searchParams.get('userlogin');
  const tokenParameter = searchParams.get('token');
  const islAdminLoginParameter = searchParams.get('isladminlogin');
  const navigate = useNavigate();

  const setErrorCase = (value) => {
    dispatch({ type: ActionType.SET_ERROR_CASE, payload: value });
  };  

  const decryptData = (encryptedData, key) => {
    if (!encryptedData || !key) {
      console.warn("Missing encrypted data or key", { encryptedData, key });
      return null;
    }
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedData, key);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      return decrypted ? JSON.parse(decrypted) : null;
    } catch (error) {
      console.error("Decryption failed:", error, { encryptedData, key });
      return null;
    }
  };

  // Initialize MSAL client on component mount
  useEffect(() => {
    const initMsal = async () => {
      const currentIssuer = localStorage.getItem(siteConstant.AUTH_ISSUER_STORAGE_KEY);
      if (currentIssuer === 'azure' && !pc) {
        console.log("Pre-initializing MSAL client");
        const msConfig = JSON.parse(localStorage.getItem(siteConstant.AZURE_CONFIG_STORAGE_KEY) || '{}');
        if (!msConfig.auth?.clientId || !msConfig.auth?.redirectUri) {
          console.error("Invalid MSAL configuration: missing clientId or redirectUri");
          return;
        }
        try {
          pc = new msal.PublicClientApplication(msConfig);
          await pc.initialize();
          setPublicClient(pc);
          console.log("MSAL client pre-initialized successfully");
        } catch (error) {
          console.error("MSAL pre-initialization failed:", error);
          pc = null;
        }
      }
    };
    
    initMsal();
  }, []);

  const initialize = useCallback(async () => {
    issuer = localStorage.getItem(siteConstant.AUTH_ISSUER_STORAGE_KEY);
    console.log("initialize called with issuer:", issuer);
    try {
      if (issuer === 'jwt') {
        const accessToken = sessionStorage.getItem(siteConstant.AUTH_TOKEN_STORAGE_KEY);
        if (accessToken) {
          const data = { accessToken };
          const response = await axios.post(`${Url}/auth/decodeToken`, data);
          const user = response.data;
          if (user.userId === undefined || user.userId === null) throw new Error("Invalid user ID in token");
          sessionStorage.setItem(siteConstant.AUTH_PROFILE_STORAGE_KEY, JSON.stringify(user));
          // Validate encrypted data
          const encryptedJoyRide = localStorage.getItem("joyrideStatus");
          if (encryptedJoyRide && !decryptData(encryptedJoyRide, user.userId.toString())) {
            console.warn("Invalid joyrideStatus, clearing");
            localStorage.removeItem("joyrideStatus");
          }
          const encryptedDashboardPerms = localStorage.getItem("dashboardPerms");
          if (encryptedDashboardPerms && !decryptData(encryptedDashboardPerms, user.userId.toString())) {
            console.warn("Invalid dashboardPerms, clearing");
            localStorage.removeItem("dashboardPerms");
          }
          const encryptedSurveyPerms = localStorage.getItem("surveyPerms");
          if (encryptedSurveyPerms && !decryptData(encryptedSurveyPerms, user.userId.toString())) {
            console.warn("Invalid surveyPerms, clearing");
            localStorage.removeItem("surveyPerms");
          }
          dispatch({
            type: ActionType.INITIALIZE,
            payload: {
              isAuthenticated: true,
              user,
            },
          });
        } else {
          dispatch({
            type: ActionType.INITIALIZE,
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } else if (issuer === 'azure') {
        // Check if we have stored user data from previous session
        const storedToken = sessionStorage.getItem(siteConstant.AUTH_TOKEN_STORAGE_KEY);
        const storedUser = sessionStorage.getItem(siteConstant.AUTH_PROFILE_STORAGE_KEY);
        
        if (storedToken && storedUser) {
          console.log("Found stored Azure session, restoring user");
          dispatch({
            type: ActionType.INITIALIZE,
            payload: {
              isAuthenticated: true,
              user: JSON.parse(storedUser),
            },
          });
          return;
        }
        
        if (!pc) {
          console.log("MSAL client not ready, initializing...");
          const msConfig = JSON.parse(localStorage.getItem(siteConstant.AZURE_CONFIG_STORAGE_KEY) || '{}');
          if (msConfig.auth?.clientId && msConfig.auth?.redirectUri) {
            pc = new msal.PublicClientApplication(msConfig);
            await pc.initialize();
            setPublicClient(pc);
          } else {
            dispatch({
              type: ActionType.INITIALIZE,
              payload: {
                isAuthenticated: false,
                user: null,
              },
            });
            return;
          }
        }
        
        const redirectResponse = await pc.handleRedirectPromise();
        if (redirectResponse) {
          console.log("Redirect response received:", redirectResponse);
          pc.setActiveAccount(redirectResponse.account);
          const user = await getProfielData(redirectResponse.account);
          dispatch({
            type: ActionType.INITIALIZE,
            payload: {
              isAuthenticated: true,
              user,
            },
          });
          navigate('/dashboard', { replace: true });
          return;
        }

        const accounts = pc.getAllAccounts();
        if (accounts.length > 0) {
          pc.setActiveAccount(accounts[0]);
          const user = await getProfielData(accounts[0]);
          dispatch({
            type: ActionType.INITIALIZE,
            payload: {
              isAuthenticated: true,
              user,
            },
          });
        } else {
          dispatch({
            type: ActionType.INITIALIZE,
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } else if (issuer === 'google') {
        const accessToken = sessionStorage.getItem(siteConstant.AUTH_TOKEN_STORAGE_KEY);
        if (accessToken) {
          const data = { accessToken };
          const response = await axios.post(`${Url}/auth/decodeToken`, data);
          const user = response.data;
          if (user.userId === undefined || user.userId === null) throw new Error("Invalid user ID in token");
          sessionStorage.setItem(siteConstant.AUTH_PROFILE_STORAGE_KEY, JSON.stringify(user));
          dispatch({
            type: ActionType.INITIALIZE,
            payload: {
              isAuthenticated: true,
              user,
            },
          });
          navigate('/dashboard', { replace: true });
        } else {
          dispatch({
            type: ActionType.INITIALIZE,
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } else {
        dispatch({
          type: ActionType.INITIALIZE,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (err) {
      console.error("Initialize error:", err);
      localStorage.removeItem("joyrideStatus");
      localStorage.removeItem("dashboardPerms");
      localStorage.removeItem("surveyPerms");
      dispatch({
        type: ActionType.INITIALIZE,
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
    // eslint-disable-next-line
  }, [dispatch]);

  const signIn = useCallback(
    async (data, method, msConfig) => {
      let user = {};
      issuer = localStorage.getItem(siteConstant.AUTH_ISSUER_STORAGE_KEY);
      console.log("signIn called with:", { issuer, data, method });
      try {
        if (issuer === 'jwt') {
          dispatch({
            type: ActionType.SIGN_IN,
            payload: {
              isAuthenticated: false,
              user: null,
              errorCase: null,
            },
          });
          const response = await axios.post(`${Url}/auth/login`, data);
          console.log("--response--", response);
          const userProfile = response.data;
          console.log("==userProfile==", userProfile);
          if (userProfile.changePassword === 1 && userProfile.key) {
            localStorage.setItem("resetKey", userProfile.key);
          }
          const token = response.headers["x-access-token"];
          if (!token) throw new Error("No access token received");
          const tokenData = { accessToken: token };
          const responseUser = await axios.post(`${Url}/auth/decodeToken`, tokenData);
          user = responseUser.data;
          if (user.userId === undefined || user.userId === null) throw new Error("Invalid user ID in token");
          const [joyridestatus, dashboardPerms, surveyPerms, profilePicture] = await Promise.all([
            customerApiService.getJoyride(token, user.userId).catch(err => {
              console.error("getJoyride error:", err);
              return { data: null };
            }),
            loginApiService.getDashboardIds(token, user.userId).catch(err => {
              console.error("getDashboardIds error:", err);
              return { dashIds: null };
            }),
            loginApiService.getSurveyIds(token, user.userId).catch(err => {
              console.error("getSurveyIds error:", err);
              return { surveyIds: null };
            }),
            mediaUploadApis.getProfilePicture(token, user.userId).catch(err => {
              console.error("getProfilePicture error:", err);
              return null;
            }),
          ]);
          console.log("surveyPerms:", surveyPerms);
          surveyPerms.surveyIds = surveyPerms.surveyIds || surveyPerms.SurveyIds;
          console.log("API Responses:", { joyridestatus, dashboardPerms, surveyPerms, profilePicture });
          const encryptedJoyRide = joyridestatus.data
            ? CryptoJS.AES.encrypt(JSON.stringify(joyridestatus.data), user.userId.toString()).toString()
            : null;
          const encryptedDashboardPerms = dashboardPerms.dashIds
            ? CryptoJS.AES.encrypt(JSON.stringify(dashboardPerms.dashIds), user.userId.toString()).toString()
            : null;
            console.log('surveyperms.surveyIds', surveyPerms.surveyIds);
          const encryptedSurveyPerms = surveyPerms.surveyIds
            ? CryptoJS.AES.encrypt(JSON.stringify(surveyPerms.surveyIds), user.userId.toString()).toString()
            : null;
          sessionStorage.setItem(siteConstant.AUTH_TOKEN_STORAGE_KEY, token);
          sessionStorage.setItem(siteConstant.AUTH_PROFILE_STORAGE_KEY, JSON.stringify(user));
          if (data.isChecked) {
            localStorage.setItem(siteConstant.AUTH_TOKEN_STORAGE_KEY, token);
            localStorage.setItem(siteConstant.AUTH_PROFILE_STORAGE_KEY, JSON.stringify(user));
            if (encryptedJoyRide) localStorage.setItem("joyrideStatus", encryptedJoyRide);
            if (encryptedDashboardPerms) localStorage.setItem("dashboardPerms", encryptedDashboardPerms);
            if (encryptedSurveyPerms) {
              console.log('inside encrypted survey perms');
              try {
                localStorage.setItem("surveyPerms", encryptedSurveyPerms);
              } catch (error) {
                console.error("Error storing encrypted surveyPerms:", error);
              }
            }
            localStorage.setItem("profilePicture", profilePicture ? JSON.stringify(profilePicture) : null);
          }
          dispatch({
            type: ActionType.SIGN_IN,
            payload: {
              isAuthenticated: true,
              user,
            },
          });
          // navigating to the dashboard after login
          navigate('/dashboard', { replace: true });
        } else if (issuer === 'azure') {
          console.log("Starting Azure login with popup");
          if (!pc) {
            console.error("PublicClient not initialized, initializing now");
            let msConfigParsed = msConfig || JSON.parse(localStorage.getItem(siteConstant.AZURE_CONFIG_STORAGE_KEY) || '{}');
            if (!msConfigParsed.auth?.clientId || !msConfigParsed.auth?.redirectUri) {
              throw new Error("Invalid MSAL configuration: missing clientId or redirectUri");
            }
            pc = new msal.PublicClientApplication(msConfigParsed);
            await pc.initialize();
            setPublicClient(pc);
          }
          const signInType = method || "loginPopup";
          console.log("signInType:", signInType);
          if (signInType === "loginPopup") {
            try {
              const loginResponse = await pc.loginPopup(loginRequest);
              console.log("Login popup response:", loginResponse);
              pc.setActiveAccount(loginResponse.account);
              user = await getProfielData(loginResponse.account);
              dispatch({
                type: ActionType.SIGN_IN,
                payload: {
                  isAuthenticated: true,
                  user,
                  issuer,
                },
              });
              navigate('/dashboard', { replace: true });
            } catch (error) {
              console.error("Popup error:", error);
              if (error.message.includes("Cross-Origin-Opener-Policy")) {
                console.warn("COOP error detected, polling for accounts");
                let attempts = 0;
                const maxAttempts = 5;
                const pollInterval = 1000;
                const pollAccounts = async () => {
                  while (attempts < maxAttempts) {
                    const accounts = pc.getAllAccounts();
                    if (accounts.length > 0) {
                      pc.setActiveAccount(accounts[0]);
                      user = await getProfielData(accounts[0]);
                      dispatch({
                        type: ActionType.SIGN_IN,
                        payload: {
                          isAuthenticated: true,
                          user,
                          issuer,
                        },
                      });
                      navigate('/dashboard', { replace: true });
                      return true;
                    }
                    attempts++;
                    await new Promise(resolve => setTimeout(resolve, pollInterval));
                  }
                  throw new Error("Authentication failed: No accounts found after polling");
                };
                if (await pollAccounts()) return;
              }
              throw error;
            }
          } else if (signInType === "loginRedirect") {
            await pc.loginRedirect(loginRequest);
          }
        } else if (issuer === 'google') {
          console.log("Starting Google login");
          const googleConfig = JSON.parse(localStorage.getItem(siteConstant.GOOGLE_CONFIG_STORAGE_KEY) || '{}');
          if (!googleConfig.clientId) {
            throw new Error("Invalid Google configuration: missing clientId");
          }
          
          const loadGoogleScript = () => {
            return new Promise((resolve, reject) => {
              if (window.google?.accounts?.oauth2) {
                resolve();
                return;
              }
              
              const script = document.createElement('script');
              script.src = 'https://accounts.google.com/gsi/client';
              script.async = true;
              script.defer = true;
              script.onload = () => {
                const checkGoogle = setInterval(() => {
                  if (window.google?.accounts?.oauth2) {
                    clearInterval(checkGoogle);
                    resolve();
                  }
                }, 100);
                setTimeout(() => {
                  clearInterval(checkGoogle);
                  reject(new Error('Google Identity Services timeout'));
                }, 10000);
              };
              script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
              document.head.appendChild(script);
            });
          };
          
          await loadGoogleScript();
          
          const tokenClient = window.google.accounts.oauth2.initTokenClient({
            client_id: googleConfig.clientId,
            scope: 'email profile',
            login_hint: data.userEmail,
            callback: async (tokenResponse) => {
              try {
                const response = await fetch(`https://www.googleapis.com/oauth2/v1/userinfo?access_token=${tokenResponse.access_token}`);
                const userInfo = await response.json();
                
                // Validate that authenticated email matches entered email
                if (userInfo.email.toLowerCase() !== data.userEmail.toLowerCase()) {
                  throw new Error(`Please sign in with ${data.userEmail}. You signed in with ${userInfo.email}`);
                }
                
                const googleAccount = {
                  username: userInfo.email,
                  name: userInfo.name,
                  email: userInfo.email
                };
                
                user = await getProfielData(googleAccount);
                dispatch({
                  type: ActionType.SIGN_IN,
                  payload: {
                    isAuthenticated: true,
                    user,
                    issuer,
                  },
                });
                navigate('/dashboard', { replace: true });
              } catch (error) {
                console.error("Google OAuth callback error:", error);
                throw error;
              }
            }
          });
          
          tokenClient.requestAccessToken();
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message;
        console.error("signIn error:", error, { issuer, data });
        dispatch({
          type: ActionType.SIGN_IN,
          payload: {
            isAuthenticated: false,
            user: null,
            errorCase: errorMessage,
          },
        });
      }
    },
    // eslint-disable-next-line
    [dispatch] 
  );

  const signUp = useCallback(
    async (email, name, password) => {
      const { accessToken } = await authApi.signUp({ email, name, password });
      const user = await authApi.me({ accessToken });
      sessionStorage.setItem(siteConstant.AUTH_TOKEN_STORAGE_KEY, accessToken);
      dispatch({
        type: ActionType.SIGN_UP,
        payload: {
          user,
        },
      });
    },
    [dispatch]
  );

  const signOut = useCallback(async () => {
    await loginApiService.userLogout();
    sessionStorage.removeItem(siteConstant.AUTH_TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(siteConstant.AUTH_PROFILE_STORAGE_KEY);
    sessionStorage.removeItem("dashboardPerms");
    sessionStorage.removeItem("surveyPerms");
    sessionStorage.removeItem("userDetails");
    sessionStorage.removeItem("teamDashboardTeamId");
    sessionStorage.removeItem("teamDashboardTeamName");
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith("mtpStatus_")) sessionStorage.removeItem(key);
    });
    sessionStorage.removeItem("teamPerms");
    sessionStorage.removeItem("invalidCredentialCount");
    sessionStorage.removeItem("joyrideStatus");
    sessionStorage.removeItem("profilePicture");
    sessionStorage.removeItem("strapiKey");
    localStorage.removeItem(siteConstant.AUTH_TOKEN_STORAGE_KEY);
    localStorage.removeItem(siteConstant.AUTH_PROFILE_STORAGE_KEY);
    localStorage.removeItem("dashboardPerms");
    localStorage.removeItem("surveyPerms");
    localStorage.removeItem("joyrideStatus");
    localStorage.removeItem("userDetails");
    localStorage.removeItem("remberMe");
    localStorage.removeItem("profilePicture");
    
    // Clear provider-specific caches
    if (pc) {
      await pc.clearCache();
    }

    
    dispatch({
      type: ActionType.SIGN_OUT,
      payload: {
        isAuthenticated: false,
        isInitialized: false,
        user: null,
      },
    });
  }, [dispatch]);

  const getProfielData = async (account) => {
    let user = {};
    try {
      console.log("getProfielData called with account:", account);
      const data = { userEmail: account.username };
      const response = await axios.post(`${Url}/auth/getProfile`, data);
      const token = response.headers["x-access-token"];
      if (!token) throw new Error("No access token received");
      const tokenData = { accessToken: token };
      const responseUser = await axios.post(`${Url}/auth/decodeToken`, tokenData);
      user = responseUser.data;
      if (user.userId === undefined || user.userId === null) throw new Error("Invalid user ID in token");
      const [joyridestatus, dashboardPerms, surveyPerms, profilePicture] = await Promise.all([
        customerApiService.getJoyride(token, user.userId).catch(err => {
          console.error("getJoyride error:", err);
          return { data: null };
        }),
        loginApiService.getDashboardIds(token, user.userId).catch(err => {
          console.error("getDashboardIds error:", err);
          return { dashIds: null };
        }),
        loginApiService.getSurveyIds(token, user.userId).catch(err => {
          console.error("getSurveyIds error:", err);
          return { surveyIds: null };
        }),
        mediaUploadApis.getProfilePicture(token, user.userId).catch(err => {
          console.error("getProfilePicture error:", err);
          return null;
        }),
      ]);
      const encryptedJoyRide = joyridestatus.data
        ? CryptoJS.AES.encrypt(JSON.stringify(joyridestatus.data), user.userId.toString()).toString()
        : null;
      const encryptedDashboardPerms = dashboardPerms.dashIds
        ? CryptoJS.AES.encrypt(JSON.stringify(dashboardPerms.dashIds), user.userId.toString()).toString()
        : null;
      const encryptedSurveyPerms = surveyPerms.surveyIds
        ? CryptoJS.AES.encrypt(JSON.stringify(surveyPerms.surveyIds), user.userId.toString()).toString()
        : null;
      sessionStorage.setItem(siteConstant.AUTH_TOKEN_STORAGE_KEY, token);
      sessionStorage.setItem(siteConstant.AUTH_PROFILE_STORAGE_KEY, JSON.stringify(user));
      const rememberMe = localStorage.getItem("remberMe") === "true";
      if (rememberMe) {
        if (encryptedJoyRide) localStorage.setItem("joyrideStatus", encryptedJoyRide);
        if (encryptedDashboardPerms) localStorage.setItem("dashboardPerms", encryptedDashboardPerms);
        if (encryptedSurveyPerms) localStorage.setItem("surveyPerms", encryptedSurveyPerms);
        localStorage.setItem("profilePicture", profilePicture ? JSON.stringify(profilePicture) : null);
      }
    } catch (error) {
      console.error("getProfielData error:", error);
    }
    return user;
  };

  const getAccessTocken = useCallback(async () => {
    if (!pc) {
      throw Error("MSAL client not initialized");
    }
    const account = pc.getActiveAccount();
    if (!account) {
      throw Error("No active account! Verify a user has been signed in and setActiveAccount has been called.");
    }
    let user = await getProfielData(account);
    dispatch({
      type: ActionType.SIGN_IN,
      payload: {
        isAuthenticated: true,
        isInitialized: true,
        user,
      },
    });
  }, [dispatch]);

  const islLogin = async () => {
    try {
      let cred = atob(islLoginParam);
      let dummyTokenParameter = atob(tokenParameter);
      const [beforeISLLOGIN, afterISLLOGIN] = cred.split('ISLLOGIN');
      const body = {
        "tenantId": beforeISLLOGIN,
        "userid": afterISLLOGIN
      };
      const details = await CoachesCornerApiServices.gettoken(dummyTokenParameter, body);
      if (details) {
        localStorage.setItem('issuer', 'jwt');
        const stringifiedUser = JSON.stringify(details.data);
        sessionStorage.setItem("userDetails", stringifiedUser);
        sessionStorage.setItem(siteConstant.AUTH_TOKEN_STORAGE_KEY, details.token);
        await customerApiService.getJoyride(details.token, afterISLLOGIN);
        await loginApiService.getDashboardIds(details.token, afterISLLOGIN);
        await loginApiService.getSurveyIds(details.token, afterISLLOGIN);
        await mediaUploadApis.getProfilePicture(details.token, afterISLLOGIN);
        await initialize();
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error("Error fetching sector data:", error);
    }
  };

  const islAdminLogin = async () => {
    try {
      let cred = atob(islAdminLoginParameter);
      let dummyTokenParameter = atob(tokenParameter);
      const [beforeISLLOGIN, afterISLLOGIN] = cred.split('ISLADMINLOGIN');
      const body = {
        "tenantId": beforeISLLOGIN,
        "userid": afterISLLOGIN
      };
      const details = await CoachesCornerApiServices.getAdmintoken(dummyTokenParameter, body);
      if (details) {
        localStorage.setItem('issuer', 'jwt');
        const stringifiedUser = JSON.stringify(details.data);
        sessionStorage.setItem("userDetails", stringifiedUser);
        sessionStorage.setItem(siteConstant.AUTH_TOKEN_STORAGE_KEY, details.token);
        await customerApiService.getJoyride(details.token, afterISLLOGIN);
        await loginApiService.getDashboardIds(details.token, afterISLLOGIN);
        await loginApiService.getSurveyIds(details.token, afterISLLOGIN);
        await mediaUploadApis.getProfilePicture(details.token, afterISLLOGIN);
        await initialize();
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error("Error fetching sector data:", error);
    }
  };

  const userLogin = async () => {
    try {
      let cred = atob(userLoginParam);
      let dummyTokenParameter = atob(tokenParameter);
      const [beforeISLLOGIN, afterISLLOGIN] = cred.split('USERLOGIN');
      const body = {
        "tenantId": beforeISLLOGIN,
        "userid": afterISLLOGIN
      };
      const details = await CoachesCornerApiServices.gettoken(dummyTokenParameter, body);
      if (details) {
        localStorage.setItem('issuer', 'jwt');
        const stringifiedUser = JSON.stringify(details.data);
        sessionStorage.setItem("userDetails", stringifiedUser);
        sessionStorage.setItem(siteConstant.AUTH_TOKEN_STORAGE_KEY, details.token);
        await customerApiService.getJoyride(details.token, afterISLLOGIN);
        await loginApiService.getDashboardIds(details.token, afterISLLOGIN);
        await loginApiService.getSurveyIds(details.token, afterISLLOGIN);
        await mediaUploadApis.getProfilePicture(details.token, afterISLLOGIN);
        await initialize();
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error("Error fetching sector data:", error);
    }
  };

  useEffect(() => {
    const init = async () => {
      await initialize();
    };
    init();
  }, [initialize]);

  useEffect(() => {
    if (islLoginParam !== null) {
      islLogin();
    }
    // eslint-disable-next-line
  }, [islLoginParam]);

  useEffect(() => {
    if (userLoginParam !== null) {
      userLogin();
    }
    // eslint-disable-next-line
  }, [userLoginParam]);

  useEffect(() => {
    if (islAdminLoginParameter !== null) {
      islAdminLogin();
    }
    // eslint-disable-next-line
  }, [islAdminLoginParameter]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        issuer: issuer,
        signIn,
        signUp,
        signOut,
        initialize,
        getAccessTocken,
        setErrorCase
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const AuthConsumer = AuthContext.Consumer;